import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './Destinations.css';

const Destinations = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Placeholder destinations data
  const allDestinations = [
    {
      id: 'DST001',
      cityName: 'Lagos',
      airportName: 'Murtala Muhammed International Airport',
      airportCode: 'LOS',
      country: 'Nigeria',
      status: 'Active'
    },
    {
      id: 'DST002',
      cityName: 'Abuja',
      airportName: 'Nnamdi Azikiwe International Airport',
      airportCode: 'ABV',
      country: 'Nigeria',
      status: 'Active'
    },
    {
      id: 'DST003',
      cityName: 'London',
      airportName: 'Heathrow Airport',
      airportCode: 'LHR',
      country: 'United Kingdom',
      status: 'Active'
    },
    {
      id: 'DST004',
      cityName: 'Dubai',
      airportName: 'Dubai International Airport',
      airportCode: 'DXB',
      country: 'United Arab Emirates',
      status: 'Active'
    },
    {
      id: 'DST005',
      cityName: 'New York',
      airportName: 'John F. Kennedy International Airport',
      airportCode: 'JFK',
      country: 'United States',
      status: 'Active'
    },
    {
      id: 'DST006',
      cityName: 'Accra',
      airportName: 'Kotoka International Airport',
      airportCode: 'ACC',
      country: 'Ghana',
      status: 'Active'
    },
    {
      id: 'DST007',
      cityName: 'Istanbul',
      airportName: 'Istanbul Airport',
      airportCode: 'IST',
      country: 'Turkey',
      status: 'Active'
    },
    {
      id: 'DST008',
      cityName: 'Port Harcourt',
      airportName: 'Port Harcourt International Airport',
      airportCode: 'PHC',
      country: 'Nigeria',
      status: 'Active'
    },
    {
      id: 'DST009',
      cityName: 'Kano',
      airportName: 'Mallam Aminu Kano International Airport',
      airportCode: 'KAN',
      country: 'Nigeria',
      status: 'Inactive'
    },
    {
      id: 'DST010',
      cityName: 'Frankfurt',
      airportName: 'Frankfurt Airport',
      airportCode: 'FRA',
      country: 'Germany',
      status: 'Active'
    },
    {
      id: 'DST011',
      cityName: 'Addis Ababa',
      airportName: 'Addis Ababa Bole International Airport',
      airportCode: 'ADD',
      country: 'Ethiopia',
      status: 'Active'
    },
    {
      id: 'DST012',
      cityName: 'Doha',
      airportName: 'Hamad International Airport',
      airportCode: 'DOH',
      country: 'Qatar',
      status: 'Active'
    }
  ];

  // Get unique countries for filter
  const countries = ['all', ...new Set(allDestinations.map(d => d.country))];

  // Filter destinations based on search and filters
  const filteredDestinations = allDestinations.filter(destination => {
    const matchesSearch = 
      destination.cityName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      destination.airportCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      destination.airportName.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCountry = countryFilter === 'all' || destination.country === countryFilter;
    
    const matchesStatus = statusFilter === 'all' || 
      destination.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesCountry && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredDestinations.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedDestinations = filteredDestinations.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleCountryFilterChange = (e) => {
    setCountryFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleAddDestination = () => {
    navigate('/admin/destinations/add');
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting destinations to CSV...');
  };

  const handleViewDestination = (destinationId) => {
    // Placeholder for view destination
    console.log('View destination:', destinationId);
  };

  const handleEditDestination = (destinationId) => {
    // Placeholder for edit destination
    console.log('Edit destination:', destinationId);
  };

  const handleToggleDestination = (destinationId) => {
    // Placeholder for disable/enable destination
    console.log('Toggle destination:', destinationId);
  };

  return (
    <AdminLayout pageTitle="Destinations">
      <div className="admin-destinations-page">
        {/* Page Title */}
        <div className="admin-destinations-header">
          <h1 className="admin-destinations-title">Destinations</h1>
          <p className="admin-destinations-subtitle">Manage airports and cities</p>
        </div>

        {/* Top Control Bar */}
        <div className="admin-destinations-controls">
          <div className="admin-controls-left">
            <button
              className="admin-add-destination-button"
              onClick={handleAddDestination}
            >
              + Add Destination
            </button>
            
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by city name or airport code..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={countryFilter}
              onChange={handleCountryFilterChange}
            >
              <option value="all">All Countries</option>
              {countries.filter(c => c !== 'all').map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>

            <select
              className="admin-filter-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <button className="admin-export-button" onClick={handleExport}>
              <span className="admin-export-icon">📥</span>
              Export CSV
            </button>
          </div>
        </div>

        {/* Destinations Table */}
        {paginatedDestinations.length > 0 ? (
          <>
            <div className="admin-destinations-table-wrapper">
              {/* Desktop Table */}
              <table className="admin-destinations-table">
                <thead>
                  <tr>
                    <th>Destination Name</th>
                    <th>Airport Name</th>
                    <th>Airport Code</th>
                    <th>Country</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDestinations.map((destination) => (
                    <tr key={destination.id} className="admin-destinations-table-row">
                      <td className="admin-table-cell admin-table-city">{destination.cityName}</td>
                      <td className="admin-table-cell">{destination.airportName}</td>
                      <td className="admin-table-cell admin-table-code">{destination.airportCode}</td>
                      <td className="admin-table-cell">{destination.country}</td>
                      <td className="admin-table-cell">
                        <span className={`admin-status-badge admin-status-${destination.status.toLowerCase()}`}>
                          {destination.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-actions-menu">
                          <button
                            className="admin-action-button"
                            onClick={() => handleViewDestination(destination.id)}
                          >
                            View
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleEditDestination(destination.id)}
                          >
                            Edit
                          </button>
                          <button
                            className="admin-action-button"
                            onClick={() => handleToggleDestination(destination.id)}
                          >
                            {destination.status === 'Active' ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="admin-destinations-cards">
                {paginatedDestinations.map((destination) => (
                  <div key={destination.id} className="admin-destination-card">
                    <div className="admin-destination-card-header">
                      <div className="admin-destination-card-city">{destination.cityName}</div>
                      <span className={`admin-status-badge admin-status-${destination.status.toLowerCase()}`}>
                        {destination.status}
                      </span>
                    </div>
                    <div className="admin-destination-card-body">
                      <div className="admin-destination-card-row">
                        <span className="admin-destination-card-label">Airport:</span>
                        <span className="admin-destination-card-value">{destination.airportName}</span>
                      </div>
                      <div className="admin-destination-card-row">
                        <span className="admin-destination-card-label">Code:</span>
                        <span className="admin-destination-card-value admin-destination-card-code">{destination.airportCode}</span>
                      </div>
                      <div className="admin-destination-card-row">
                        <span className="admin-destination-card-label">Country:</span>
                        <span className="admin-destination-card-value">{destination.country}</span>
                      </div>
                    </div>
                    <div className="admin-destination-card-actions">
                      <select
                        className="admin-mobile-actions-select"
                        onChange={(e) => {
                          const action = e.target.value;
                          if (action === 'view') handleViewDestination(destination.id);
                          else if (action === 'edit') handleEditDestination(destination.id);
                          else if (action === 'toggle') handleToggleDestination(destination.id);
                          e.target.value = '';
                        }}
                      >
                        <option value="">Actions...</option>
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                        <option value="toggle">{destination.status === 'Active' ? 'Disable' : 'Enable'}</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredDestinations.length)} of {filteredDestinations.length} destinations
              </div>
              <div className="admin-pagination-controls">
                <select
                  className="admin-rows-select"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="admin-page-numbers">
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="admin-page-ellipsis">...</span>}
                          <button
                            className={`admin-page-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="admin-empty-state">
            <div className="admin-empty-icon">✈️</div>
            <h3 className="admin-empty-title">No destinations found</h3>
            <p className="admin-empty-text">
              {searchQuery || countryFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'Get started by adding your first destination'}
            </p>
            <button
              className="admin-empty-action-button"
              onClick={handleAddDestination}
            >
              + Add Destination
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Destinations;

